import { API_BASE_URL } from './utils';
import { markSessionActive, clearSessionActive, getMdtEventId } from './mdtSessionStorage';
import { registerWebPush, unregisterWebPush, unregisterNativeDevice } from './pushSession';
import { startLocationWatch, stopLocationWatch, startForegroundService, stopForegroundService } from './nativeLocation';
import { initNativePush } from './nativePush';
import type { Resource, User } from '../types';

type UnitStatus = 'available' | 'busy' | 'enroute' | 'onscene' | 'onbreak' | 'conveying';

interface SessionContext {
  resource: Resource;
  user: User;
  token: string;
}

let pingInterval: ReturnType<typeof setInterval> | null = null;
let wakeLock: WakeLockSentinel | null = null;
let wakeLockCancelled = false;
let unitStatus: UnitStatus = 'available';
let activeSession: SessionContext | null = null;
let lastLat = 0;
let lastLng = 0;
let hasPosition = false;

export function setSessionUnitStatus(status: UnitStatus): void {
  unitStatus = status;
}

export function isSessionRunning(): boolean {
  return activeSession !== null;
}

export function dispatchMdtForceLogout(reason?: string): void {
  window.dispatchEvent(
    new CustomEvent('mdt:force_logout', { detail: { reason: reason || 'Session ended by Command' } })
  );
}

async function handleSessionAuthResponse(res: Response | null): Promise<void> {
  if (!res?.ok) return;
  try {
    const data = await res.json();
    if (data.forceLogout) {
      dispatchMdtForceLogout(data.reason);
    }
  } catch {
    /* ignore */
  }
}

async function postPing(ctx: SessionContext): Promise<void> {
  const lat = hasPosition ? lastLat : 40.7128 + (Math.random() - 0.5) * 0.02;
  const lng = hasPosition ? lastLng : -74.006 + (Math.random() - 0.5) * 0.02;
  const eventId = getMdtEventId(ctx.resource);
  const res = await fetch(`${API_BASE_URL}/tracking/ping`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${ctx.token}` },
    body: JSON.stringify({
      resourceId: ctx.resource.id,
      resourceName: ctx.resource.name,
      userId: ctx.user.id,
      userName: `${ctx.user.firstName} ${ctx.user.lastName}`,
      latitude: lat,
      longitude: lng,
      status: unitStatus,
      eventId,
    }),
  }).catch(() => null);
  await handleSessionAuthResponse(res);
}

function startGps(ctx: SessionContext): void {
  stopGps(false);
  hasPosition = false;

  startLocationWatch((lat, lng) => {
    lastLat = lat;
    lastLng = lng;
    hasPosition = true;
  });

  postPing(ctx);
  pingInterval = setInterval(() => postPing(ctx), 10000);
}

function stopGps(sendDisconnect: boolean): void {
  if (pingInterval) {
    clearInterval(pingInterval);
    pingInterval = null;
  }
  stopLocationWatch(false);

  if (sendDisconnect && activeSession) {
    fetch(`${API_BASE_URL}/tracking/disconnect`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${activeSession.token}`,
      },
      body: JSON.stringify({ resourceId: activeSession.resource.id }),
    }).catch(() => {});
  }
}

async function acquireWakeLock(): Promise<void> {
  wakeLockCancelled = false;
  try {
    const nav = navigator as Navigator & { wakeLock?: { request: (type: 'screen') => Promise<WakeLockSentinel> } };
    if (nav.wakeLock?.request) {
      wakeLock = await nav.wakeLock.request('screen');
    }
  } catch { /* ignore */ }
}

function releaseWakeLock(): void {
  wakeLockCancelled = true;
  try { wakeLock?.release(); } catch { /* ignore */ }
  wakeLock = null;
}

document.addEventListener('visibilitychange', () => {
  if (!wakeLockCancelled && document.visibilityState === 'visible' && activeSession) {
    acquireWakeLock();
  }
});

async function activateSessionServices(ctx: SessionContext): Promise<void> {
  activeSession = ctx;
  markSessionActive();
  startGps(ctx);
  await acquireWakeLock();
  await registerWebPush(ctx.token, ctx.resource.id);
  await initNativePush(ctx.token, ctx.resource.id);
  await startForegroundService(ctx.resource.name);
  const eventId = getMdtEventId(ctx.resource);
  const heartbeatRes = await fetch(`${API_BASE_URL}/mdt/heartbeat`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${ctx.token}` },
    body: JSON.stringify({ resourceId: ctx.resource.id, eventId }),
  }).catch(() => null);
  await handleSessionAuthResponse(heartbeatRes);
}

export async function startSession(ctx: SessionContext): Promise<void> {
  await activateSessionServices(ctx);
}

export async function resumeSession(ctx: SessionContext): Promise<void> {
  await activateSessionServices(ctx);
  const eventId = getMdtEventId(ctx.resource);
  const heartbeatRes = await fetch(`${API_BASE_URL}/mdt/heartbeat`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${ctx.token}` },
    body: JSON.stringify({ resourceId: ctx.resource.id, eventId, resumed: true }),
  }).catch(() => null);
  await handleSessionAuthResponse(heartbeatRes);
}

/** Stop session — only call on explicit logout, NOT on force close. */
export async function stopSession(
  token: string | null,
  resourceId?: string,
  eventId?: string
): Promise<void> {
  stopGps(true);
  releaseWakeLock();
  await stopForegroundService();
  await unregisterWebPush(token);
  await unregisterNativeDevice(token);

  if (token && resourceId && !resourceId.startsWith('temp-')) {
    await fetch(`${API_BASE_URL}/resources/${encodeURIComponent(resourceId)}/release`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(eventId ? { eventId } : {}),
    }).catch(() => {});
  }

  activeSession = null;
  clearSessionActive();
}

export function getActiveSession(): SessionContext | null {
  return activeSession;
}
